import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { checkCryptoIntegrity } from '../utils/cryptoIntegrityCheck';
import logger from '../utils/logger';
import './CryptoIntegrityGuard.css';

/**
 * CryptoIntegrityGuard component
 *
 * Runs the Web Crypto CSPRNG sanity check on mount and blocks the app if the
 * check fails, showing a user-friendly error message.
 *
 * This addresses GS-A_4367 / BSI-TR-03116-1 §3.8 G1: the product must not
 * silently continue if crypto.getRandomValues is absent or returns obviously
 * broken output (all-zero, constant-value).
 *
 * See: docs/audit/GS-A_4367_Random_Number_Generation.md §7
 */
const CryptoIntegrityGuard = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isOk, setIsOk] = useState(false);
  const [errorReason, setErrorReason] = useState('');

  useEffect(() => {
    logger.info('CryptoIntegrityGuard: running CSPRNG sanity check');
    const result = checkCryptoIntegrity();
    setIsOk(result.ok);
    if (!result.ok) {
      setErrorReason(result.reason);
    }
    setIsChecking(false);
  }, []);

  if (isChecking) {
    return <div className="crypto-guard-checking" />;
  }

  if (!isOk) {
    return (
      <div className="crypto-guard-container">
        <div className="crypto-guard-error-content">
          <div className="crypto-guard-error-icon">🔒</div>
          <h1 className="crypto-guard-error-title">Secure Messaging Unavailable</h1>
          <p className="crypto-guard-error-message">
            This application requires a functional cryptographic random number generator,
            which could not be verified in your current browser environment.
          </p>
          <p className="crypto-guard-error-detail">{errorReason}</p>
          <div className="crypto-guard-troubleshooting">
            <h3>How to fix this:</h3>
            <ul className="crypto-guard-list">
              <li>
                <strong>Try a supported browser:</strong>
                <br />Use a recent version of Chrome, Edge, or Firefox.
              </li>
              <li>
                <strong>Disable browser extensions:</strong>
                <br />A security or privacy extension may be interfering with the
                browser&apos;s cryptography APIs. Try disabling extensions and reloading.
              </li>
              <li>
                <strong>Check your browser settings:</strong>
                <br />Ensure JavaScript and Web Cryptography are not blocked by your
                browser or network security policy.
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

CryptoIntegrityGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CryptoIntegrityGuard;
